import * as z from 'zod/v4';

import { invertObject, zodEnumFromObjectKeys } from '../utils/entry.js';

/**
 * TS type for product name
 */
export type Product =
  | 'Stryktipset'
  | 'Europatipset'
  | 'Matchen'
  | 'Bomben'
  | 'Maltipset'
  | 'TopptipsetStryk'
  | 'TopptipsetExtra'
  | 'Topptipset'
  | 'Powerplay'
  | 'Sportkrysset'
  | 'Fulltraff'
  | 'Challenge';

/**
 * Object mapping between product id and numeric product id
 */
const COUPON_PRODUCT_MAP_VARIANTS = {
  Stryktipset: 1,
  Europatipset: 2,
  Matchen: 6,
  Bomben: 7,
  Maltipset: 8,
  TopptipsetStryk: 23,
  TopptipsetExtra: 24,
  Topptipset: 25,
  Powerplay: 28,
  Fulltraff: 38,
  Challenge: 45,
} as const satisfies Record<Exclude<Product, 'Sportkrysset'>, number>;

export const PRODUCT_MAP_VARIANTS = {
  ...COUPON_PRODUCT_MAP_VARIANTS,
  Sportkrysset: 30,
} as const satisfies Record<Product, number>;
export type ProductMapVariants = typeof PRODUCT_MAP_VARIANTS;

/**
 * Zod enum mapping for product id to product name
 * Used where product id must be a Client product id.
 * For parsing API data, utilize `productSchema` instead.
 */
export const productEnum = zodEnumFromObjectKeys(PRODUCT_MAP_VARIANTS);

export const couponProductEnum = zodEnumFromObjectKeys(COUPON_PRODUCT_MAP_VARIANTS);
export type CouponProduct = z.infer<typeof couponProductEnum>;

/**
 * Discriminated union type product constraint
 */
export type ProductConstraint = {
  [P in keyof typeof COUPON_PRODUCT_MAP_VARIANTS]: {
    type: P;
    id: (typeof COUPON_PRODUCT_MAP_VARIANTS)[P];
  };
}[keyof typeof COUPON_PRODUCT_MAP_VARIANTS];

/**
 * Utility function to get product constraint as a correct union type.
 *
 * @param product Product identifier
 * @returns Product constraint object
 */
export function getProductConstraint(product: CouponProduct): ProductConstraint {
  return {
    type: product,
    id: COUPON_PRODUCT_MAP_VARIANTS[product],
  } as ProductConstraint;
}

/**
 * Object mapping between numeric product id and product id
 */
export const PRODUCT_ID_MAP_VARIANTS = invertObject<Product, number>(PRODUCT_MAP_VARIANTS);

/**
 * Object mapping between product id and product draw API
 */
export const PRODUCT_DRAW_API = {
  Stryktipset: 'stryktipset',
  Europatipset: 'europatipset',
  Matchen: 'matchen',
  Bomben: 'bomben',
  Maltipset: 'maltipset',
  TopptipsetStryk: 'topptipsetfamily',
  TopptipsetExtra: 'topptipsetfamily',
  Topptipset: 'topptipsetfamily',
  Powerplay: 'powerplay',
  Sportkrysset: 'sportkrysset',
  Fulltraff: 'fulltraff',
  Challenge: 'challenge',
} as const satisfies Record<Product, string>;
export type ProductDrawAPI = typeof PRODUCT_DRAW_API;

/**
 * Object mapping between product id and ITS product id
 */
export const ITS_PRODUCT_ID = {
  Stryktipset: 'STRYK',
  Europatipset: 'ITA',
  Matchen: 'MATCHEN',
  Bomben: 'BOMBEN',
  Maltipset: 'MAL',
  TopptipsetStryk: 'T8STRYK',
  TopptipsetExtra: 'T8EXTRA',
  Topptipset: 'T8',
  Powerplay: 'POWERPLAY',
  Sportkrysset: 'SPORTJOKER',
  Fulltraff: 'ANTMAL',
  Challenge: 'TMIX',
} as const satisfies Record<Product, string>;

const stryktipsetUnion = z
  .union([
    // Stryktipset flavors
    z.literal('STRYKTIPSET'),
    z.literal(ITS_PRODUCT_ID.Stryktipset),
    z.literal(PRODUCT_MAP_VARIANTS.Stryktipset),
  ])
  .transform<Product>(() => 'Stryktipset');
const europatipsetUnion = z
  .union([
    // Europatipset flavors
    z.literal('EUROPATIPSET'),
    z.literal(ITS_PRODUCT_ID.Europatipset),
    z.literal(PRODUCT_MAP_VARIANTS.Europatipset),
  ])
  .transform<Product>(() => 'Europatipset');
const matchenUnion = z
  .union([
    // Matchen flavors
    z.literal('MATCHEN'),
    z.literal(ITS_PRODUCT_ID.Matchen),
    z.literal(PRODUCT_MAP_VARIANTS.Matchen),
  ])
  .transform<Product>(() => 'Matchen');
const bombenUnion = z
  .union([
    // Bomben flavors
    z.literal('BOMBEN'),
    z.literal(ITS_PRODUCT_ID.Bomben),
    z.literal(PRODUCT_MAP_VARIANTS.Bomben),
  ])
  .transform<Product>(() => 'Bomben');
const maltipsetUnion = z
  .union([
    // Måltipset flavors
    z.literal('MALTIPSET'),
    z.literal(ITS_PRODUCT_ID.Maltipset),
    z.literal(PRODUCT_MAP_VARIANTS.Maltipset),
  ])
  .transform<Product>(() => 'Maltipset');
const topptipsetStrykUnion = z
  .union([
    // TopptipsetStryk flavors
    z.literal('TOPPTIPSETSTRYK'),
    z.literal(ITS_PRODUCT_ID.TopptipsetStryk),
    z.literal(PRODUCT_MAP_VARIANTS.TopptipsetStryk),
  ])
  .transform<Product>(() => 'TopptipsetStryk');
const topptipsetExtraUnion = z
  .union([
    // TopptipsetExtra flavors
    z.literal('TOPPTIPSETEXTRA'),
    z.literal(ITS_PRODUCT_ID.TopptipsetExtra),
    z.literal(PRODUCT_MAP_VARIANTS.TopptipsetExtra),
  ])
  .transform<Product>(() => 'TopptipsetExtra');
const topptipsetUnion = z
  .union([
    // Topptipset flavors
    z.literal('TOPPTIPSET'),
    z.literal(ITS_PRODUCT_ID.Topptipset),
    z.literal(PRODUCT_MAP_VARIANTS.Topptipset),
  ])
  .transform<Product>(() => 'Topptipset');
const powerplayUnion = z
  .union([
    // Powerplay flavors
    z.literal('POWERPLAY'),
    z.literal(ITS_PRODUCT_ID.Powerplay),
    z.literal(PRODUCT_MAP_VARIANTS.Powerplay),
  ])
  .transform<Product>(() => 'Powerplay');
const sportkryssUnion = z
  .union([
    // Sportkryss flavors
    z.literal('SPORTJOKER'),
    z.literal('SPORTKRYSS'),
    z.literal(ITS_PRODUCT_ID.Sportkrysset),
    z.literal(PRODUCT_MAP_VARIANTS.Sportkrysset),
  ])
  .transform<Product>(() => 'Sportkrysset');
const fulltraffUnion = z
  .union([
    // Fullträff flavors
    z.literal('FULLTRAFF'),
    z.literal(ITS_PRODUCT_ID.Fulltraff),
    z.literal(PRODUCT_MAP_VARIANTS.Fulltraff),
  ])
  .transform<Product>(() => 'Fulltraff');
const challengeUnion = z
  .union([
    // Challange flavors
    z.literal('CHALLENGE'),
    z.literal(ITS_PRODUCT_ID.Challenge),
    z.literal(PRODUCT_MAP_VARIANTS.Challenge),
  ])
  .transform<Product>(() => 'Challenge');

export const productSchema = z.preprocess(
  (val) => (typeof val === 'string' ? val.toUpperCase() : val),
  z.union([
    stryktipsetUnion,
    europatipsetUnion,
    matchenUnion,
    bombenUnion,
    maltipsetUnion,
    topptipsetStrykUnion,
    topptipsetExtraUnion,
    topptipsetUnion,
    powerplayUnion,
    sportkryssUnion,
    fulltraffUnion,
    challengeUnion,
  ])
);

/**
 * Object mapping between product id and product names
 */
export const PRODUCT_NAME = {
  Stryktipset: 'Stryktipset',
  Europatipset: 'Europatipset',
  Matchen: 'Matchen',
  Bomben: 'Bomben',
  Maltipset: 'Måltipset',
  TopptipsetStryk: 'Topptipset Stryk',
  TopptipsetExtra: 'Topptipset Extra',
  Topptipset: 'Topptipset',
  Powerplay: 'Powerplay',
  Sportkrysset: 'Sportkryss',
  Fulltraff: 'Fullträff',
  Challenge: 'Challenge',
} as const satisfies Record<Product, string>;

/**
 * Zod enum mapping for product id to numeric product id
 */
export const productIdEnum = z.enum(PRODUCT_MAP_VARIANTS);

/**
 * TS type for product id
 */
export type ProductId = z.infer<typeof productIdEnum>;
export type ProductIdNumeric = ProductMapVariants[keyof ProductMapVariants];

/**
 * TS type for sub product
 */
export type SubProduct = 'EmTipset' | 'VmTipset';

/**
 * Checks if a given string is a valid Product.
 *
 * @param value - The string to check.
 * @returns True if the string is a valid Product, otherwise false.
 */
export function isProduct(value: unknown): value is Product {
  if (typeof value !== 'string') {
    return false;
  }

  return value in PRODUCT_MAP_VARIANTS;
}

/**
 * Checks if a given string is a valid Coupon product.
 *
 * @param value The string to check
 * @returns True if the string is a valid Coupon Product
 */
export function isCouponProduct(value: unknown): value is CouponProduct {
  if (typeof value !== 'string') {
    return false;
  }

  return value in COUPON_PRODUCT_MAP_VARIANTS;
}

/**
 * Coupon product schema, returns the product if viable product to create a coupon for.
 */
export const couponProductSchema = productSchema.transform((product, ctx) => {
  if (!isCouponProduct(product)) {
    ctx.addIssue({
      code: 'custom',
      message: `Unsupported product ${product}`,
    });
    return z.NEVER;
  }

  return product;
});
